/*
  PLEASE BE MINDFUL OF WHAT IMPORTS ARE USED IN THIS FILE.

  THIS IS INCLUDED IN THE MAIN LOAD OF THE APPLICATION AND
  THUS ANY DEPENDENCIES WILL BE ADDED TO THE BUNDLES.
*/
import { Inject, Injectable } from '@angular/core';

import {
  AuthService,
  GenericError,
  MfaRequiredError,
} from '@auth0/auth0-angular';
import { DtrumApi } from '@dynatrace/dtrum-api-types/dtrum';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap } from 'rxjs/operators';

import { SessionStorageService } from '@/app/services';
import { DT_RUM } from '@/app/services/injectable-dynatrace';
import { UserService } from '@/app/services/user.service';
import { environment } from '@/environments/environment';
import { ContextActions } from '../actions/context.actions';
import { ErrorActions } from '../actions/error.actions';
import { LoggedInUserActions } from '../actions/logged-in-user.actions';

@Injectable()
export class AppUserEffects {
  // Effects
  loggedInUser$;

  idpError$;

  performLogout$;
  performLogin$;
  clearErrorStateOnLogin$;

  idpUserUpdated$;

  userMonitoring$;

  constructor(
    actions$: Actions,
    auth: AuthService,
    @Inject(DT_RUM) dtrum: DtrumApi | undefined,
    userService: UserService,
    sessionStorageService: SessionStorageService,
  ) {
    this.performLogout$ = createEffect(
      () => {
        return actions$.pipe(
          ofType(LoggedInUserActions.logout),
          tap((action) => {
            sessionStorageService.clear();
            userService.logout({
              logoutParams: {
                returnTo: environment.applicationRoot.toString(),
              },
              ...(action ?? {}),
            });
          }),
        );
      },
      { dispatch: false },
    );
    this.performLogin$ = createEffect(
      () => {
        return actions$.pipe(
          ofType(LoggedInUserActions.login),
          tap(() => auth.loginWithRedirect()),
        );
      },
      { dispatch: false },
    );

    this.clearErrorStateOnLogin$ = createEffect(() => {
      return actions$.pipe(
        ofType(LoggedInUserActions.login),
        map(() => ErrorActions.removeIdPError()),
      );
    });

    this.idpUserUpdated$ = createEffect(() => {
      return auth.user$.pipe(
        filter((user) => !!user),
        map((user) => LoggedInUserActions.setIdPUser({ user: user! })),
      );
    });

    this.loggedInUser$ = createEffect(() => {
      return actions$.pipe(
        ofType(ContextActions.loadSuccess),
        map((context) =>
          LoggedInUserActions.setFirsTechUser({
            ftUser: context.initialAppContext.currentUser,
          }),
        ),
      );
    });

    this.idpError$ = createEffect(() => {
      return auth.error$.pipe(
        map((error) => {
          if (error instanceof MfaRequiredError) {
            const mfaError = error as MfaRequiredError;
            return ErrorActions.addMFAError({
              code: mfaError.error,
              description: mfaError.error_description,
            });
          } else if (error instanceof GenericError) {
            return ErrorActions.addIdPError({
              code: error?.error || 'unknown_error',
              description:
                error?.error_description ||
                'An unknown error occurred during authentication.',
            });
          } else {
            return ErrorActions.addIdPError({
              code: 'unknown_error',
              description:
                error.message ||
                'An unknown error occurred during authentication.',
            });
          }
        }),
      );
    });

    // Not an effect, but a subscription to when a user becomes authenticated.
    this.userMonitoring$ = createEffect(
      () => {
        return auth.user$.pipe(
          filter((user) => !!user && !!dtrum),
          map((user) => user!),
          tap((user) => {
            dtrum?.identifyUser(user.email ?? user.sub!);
          }),
        );
      },
      { dispatch: false },
    );
  }
}
